'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, } from 'react';
import { useParams } from './params-provider';
import { useLocation } from '../location-provider';
const PAGE_KEY = 'page';
const PAGE_SIZE_KEY = 'size';
export const PaginationContext = createContext({
    goToPrevPage: () => { },
    goToNextPage: () => { },
    goToPage: (_page) => { },
    activePage: 1,
    pageSize: 10,
    paramsIsLoading: true,
});
PaginationContext.displayName = 'PaginationContext';
export const PaginationProvider = ({ children, defaultPageSize = 10, defaultPage = 1, }) => {
    const { getParam, addParam, paramsIsLoading, params, removeParam } = useParams();
    const { pathname } = useLocation();
    const pageInitialized = useRef(false);
    const initializedPathname = useRef(null);
    const [pageState, setPageState] = useState({
        activePage: defaultPage,
        pageSize: defaultPageSize,
        pageInitialized: false,
    });
    const preparePage = useCallback(() => {
        if (pageInitialized.current && initializedPathname.current === pathname) {
            return;
        }
        if (paramsIsLoading)
            return;
        const pageParam = getParam(PAGE_KEY);
        const sizeParam = getParam(PAGE_SIZE_KEY);
        if (!pageParam && !sizeParam) {
            setPageState({
                activePage: defaultPage,
                pageSize: defaultPageSize,
                pageInitialized: true,
            });
            initializedPathname.current = pathname;
            pageInitialized.current = true;
            return;
        }
        const page = pageParam ? parseInt(pageParam) : null;
        const size = sizeParam ? parseInt(sizeParam) : null;
        setPageState({
            activePage: page !== null && page !== void 0 ? page : defaultPage,
            pageSize: size !== null && size !== void 0 ? size : defaultPageSize,
            pageInitialized: true,
        });
        initializedPathname.current = pathname;
        pageInitialized.current = true;
    }, [getParam, defaultPage, defaultPageSize, paramsIsLoading, pathname]);
    const goToPage = useCallback((page, size) => {
        const validPage = Math.max(1, page);
        const validSize = size && size > 0 ? size : pageState.pageSize;
        setPageState((prev) => (Object.assign(Object.assign({}, prev), { activePage: validPage, pageSize: validSize })));
        if (validPage !== defaultPage) {
            addParam(PAGE_KEY, `${validPage}`);
        }
        else {
            removeParam(PAGE_KEY);
        }
        if (validSize !== defaultPageSize) {
            addParam(PAGE_SIZE_KEY, `${validSize}`);
        }
        else {
            removeParam(PAGE_SIZE_KEY);
        }
    }, [addParam, removeParam, defaultPage, defaultPageSize, pageState.pageSize]);
    const goToPrevPage = useCallback(() => {
        const activePage = Math.max(1, pageState.activePage - 1);
        const pageSize = pageState.pageSize;
        goToPage(activePage, pageSize);
    }, [goToPage, pageState.activePage, pageState.pageSize]);
    const goToNextPage = useCallback(() => {
        const activePage = pageState.activePage + 1;
        const pageSize = pageState.pageSize;
        goToPage(activePage, pageSize);
    }, [goToPage, pageState.activePage, pageState.pageSize]);
    useEffect(() => {
        preparePage();
    }, [preparePage, params]);
    const contextValue = useMemo(() => ({
        goToNextPage,
        goToPrevPage,
        goToPage,
        activePage: pageState.activePage,
        pageSize: pageState.pageSize,
        paramsIsLoading,
    }), [
        goToNextPage,
        goToPrevPage,
        goToPage,
        pageState.activePage,
        pageState.pageSize,
        paramsIsLoading,
    ]);
    return (_jsx(PaginationContext.Provider, { value: contextValue, children: children }));
};
export const usePagination = () => useContext(PaginationContext);
export default PaginationProvider;
